import { datadogLogs } from '@datadog/browser-logs';
import { useSession } from '@descope/react-sdk';
import { createContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { PharmacySelection } from '@app/pages/pharmacy-selection/pharmacy-selection';
import { GlobalSpinner } from '@components/sds/global-spinner/global-spinner';
import { OrderDirectionEnum } from 'generated-schema-types';
import type {
  PharmacyContextValue,
  PharmacySearchFilters,
  SelectedPharmacy,
} from './types';
import { usePharmacies, type PharmacySortDescriptor } from './use-pharmacies';

const STORAGE_KEY = 'selected-pharmacy';

export const PharmacyContext = createContext<PharmacyContextValue | undefined>(
  undefined
);

interface PharmacyProviderProperties {
  children: ReactNode;
}

export const PharmacyProvider = ({ children }: PharmacyProviderProperties) => {
  const { isAuthenticated } = useSession();
  const [searchFilters, setSearchFilters] = useState<PharmacySearchFilters>({});
  const [sortDescriptor, setSortDescriptor] = useState<PharmacySortDescriptor>({
    field: 'name',
    direction: OrderDirectionEnum.Asc,
  });
  const {
    pharmacies,
    isLoading: isLoadingPharmacies,
    hasNextPage,
    hasPreviousPage,
    next,
    previous,
  } = usePharmacies({
    npi: searchFilters.npi ? { eq: searchFilters.npi } : undefined,
    zip: searchFilters.zip ? { ilike: `${searchFilters.zip}%` } : undefined,
    sort: sortDescriptor,
  });
  const [selectedPharmacy, setSelectedPharmacy] =
    useState<SelectedPharmacy | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSelectingPharmacy, setIsSelectingPharmacy] = useState(false);
  const isInitialSetupComplete = useRef(false);

  // Initialize: load from sessionStorage, auto-select single pharmacy, or enter selection mode
  useEffect(() => {
    if (
      !isAuthenticated ||
      isLoadingPharmacies ||
      isInitialSetupComplete.current
    ) {
      return;
    }

    // Load stored selection from sessionStorage
    let hasStoredSelection = false;

    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);

      if (stored) {
        const parsed = JSON.parse(stored) as SelectedPharmacy;
        setSelectedPharmacy(parsed);
        hasStoredSelection = true;
      }
    } catch (error) {
      datadogLogs.logger.error(
        'Failed to parse stored pharmacy selection',
        { storageKey: STORAGE_KEY },
        error as Error
      );
      sessionStorage.removeItem(STORAGE_KEY);
    }

    // Mark as initialized (enables UI rendering)
    if (!isInitialized) {
      setIsInitialized(true);
    }

    // Only proceed with auto-selection/selection mode if pharmacies have loaded
    if (pharmacies.length === 0) {
      return;
    }

    // Mark setup as complete to prevent re-running
    isInitialSetupComplete.current = true;

    // Auto-select if only one pharmacy and no stored selection
    if (pharmacies.length === 1 && !hasStoredSelection) {
      const pharmacy = pharmacies[0];
      setSelectedPharmacy(pharmacy);

      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pharmacy));
      } catch (error) {
        datadogLogs.logger.error(
          'Failed to store pharmacy selection',
          { pharmacyId: pharmacy.id },
          error as Error
        );
      }
    }
    // Enter selection mode if multiple pharmacies and no selection
    else if (pharmacies.length > 1 && !hasStoredSelection) {
      setIsSelectingPharmacy(true);
    }
  }, [isAuthenticated, isLoadingPharmacies, pharmacies, isInitialized]);

  const handleSelectPharmacy = (pharmacy: SelectedPharmacy) => {
    setSelectedPharmacy(pharmacy);
    setIsSelectingPharmacy(false);

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pharmacy));
    } catch (error) {
      datadogLogs.logger.error(
        'Failed to store pharmacy selection',
        { pharmacyId: pharmacy.id },
        error as Error
      );
    }
  };

  const clearSelection = () => {
    setSelectedPharmacy(null);
    setIsSelectingPharmacy(true);

    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      datadogLogs.logger.error(
        'Failed to clear pharmacy selection',
        {},
        error as Error
      );
    }
  };

  const needsSelection = isSelectingPharmacy && !selectedPharmacy;

  const contextValue: PharmacyContextValue = {
    selectedPharmacy,
    setSelectedPharmacy: handleSelectPharmacy,
    isLoading: !isInitialized || isLoadingPharmacies,
    needsSelection,
    clearSelection,
    pharmacies,
    searchFilters,
    setSearchFilters,
    hasNextPage,
    hasPreviousPage,
    nextPage: next,
    previousPage: previous,
  };

  return (
    <PharmacyContext.Provider value={contextValue}>
      {needsSelection ? (
        <PharmacySelection
          pharmacies={pharmacies}
          onSelectPharmacy={handleSelectPharmacy}
          searchFilters={searchFilters}
          onSearchFiltersChange={setSearchFilters}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onNextPage={next}
          onPreviousPage={previous}
          isLoading={isLoadingPharmacies}
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
        />
      ) : (
        children
      )}

      <GlobalSpinner isVisible={!isInitialized} />
    </PharmacyContext.Provider>
  );
};
